'use client';

import { useCallback, useContext } from 'react';
import { FullScreenContext } from '@/app/contexts';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { getCurrentFileInfo } from '@/shared/clientFunctions';
import type { MediaSource, MediaType } from '@/shared/types';
import type { GalleryMediaKind } from '@/shared/mobile-service/lib/media-kind';

/** One collected image or video the evidence viewer can open. */
export interface CollectedMediaTarget {
  url: string;
  kind: GalleryMediaKind;
  filename: string | null;
  /** Provenance webp/mp4 when the original bytes are not browser-native. */
  previewUrl?: string | null;
}

function mediaTypeFor(kind: GalleryMediaKind): MediaType {
  if (kind === 'video') return 'video';
  if (kind === 'audio') return 'audio';
  return 'image';
}

/**
 * What the preview lookup found. `skipped` means no session, so the lookup never ran. `error`
 * means no file record came back: `getCurrentFileInfo` returns null for a missing file, a server
 * error, a network drop, and an archive-member path alike. Only `miss` is a real "the file exists
 * and has no preview" answer.
 */
export interface CollectedMediaInfo {
  status: 'ok' | 'miss' | 'skipped' | 'error';
  previewUrl: string | null;
  fileUuid: string | null;
  fileUrl: string | null;
}

export interface ResolveCollectedMediaInfo {
  (url: string): Promise<CollectedMediaInfo>;
}

const NO_INFO = { previewUrl: null, fileUuid: null, fileUrl: null } as const;

/**
 * Ask provenance for the web-friendly transcode the evidence viewer already uses.
 *
 * HEIC and other limited formats are not decodable in the browser. When the
 * image pipeline wrote a preview, file-info returns it. A miss means there is
 * nothing to show.
 */
export async function resolveCollectedMediaInfo(
  url: string,
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
): Promise<CollectedMediaInfo> {
  if (!cellId || !accessToken) return { status: 'skipped', ...NO_INFO };
  try {
    const info = await getCurrentFileInfo(url, cellId, accessToken);
    if (!info) return { status: 'error', ...NO_INFO };
    const previewUrl = info.previewUrl?.trim() || null;
    return {
      status: previewUrl ? 'ok' : 'miss',
      previewUrl,
      fileUuid: info.fileUuid?.trim() || null,
      fileUrl: info.fileUrl?.trim() || null,
    };
  } catch {
    return { status: 'error', ...NO_INFO };
  }
}

export async function resolveCollectedMediaPreview(
  url: string,
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
): Promise<string | null> {
  return (await resolveCollectedMediaInfo(url, cellId, accessToken)).previewUrl;
}

/** Open a collected file in the same fullscreen viewer chat uses for evidence. */
export function useOpenCollectedMedia() {
  const { setFullscreenMediaItem, setIsFullscreen } = useContext(FullScreenContext);
  const { cellId, accessToken } = useCellServiceSession();

  const resolvePreview = useCallback(
    (url: string) => resolveCollectedMediaPreview(url, cellId, accessToken),
    [accessToken, cellId],
  );
  const resolvePreviewInfo = useCallback(
    (url: string) => resolveCollectedMediaInfo(url, cellId, accessToken),
    [accessToken, cellId],
  );

  const open = useCallback(
    (media: CollectedMediaTarget) => {
      const displayUrl = media.previewUrl?.trim() || media.url;
      const item: MediaSource = {
        sourceFilePath: media.url,
        sourceUrl: media.url,
        originalUrl: media.url,
        mediaUrl: displayUrl,
        mediaSrc: displayUrl,
        mediaType: mediaTypeFor(media.kind),
      };
      setFullscreenMediaItem(item);
      setIsFullscreen(true);
    },
    [setFullscreenMediaItem, setIsFullscreen],
  );

  return { open, resolvePreview, resolvePreviewInfo };
}
